/**
 * NAID + assets API hooks.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { NaidOverviewRow, NaidRecord, CompanyAsset, Paginated } from '@shared/types';
import { naidUpdateInput, assetAssignInput, assetReturnInput } from '@shared/schemas/index.js';
import { z } from 'zod';

export type NaidUpdateBody = z.input<typeof naidUpdateInput>;
export type AssetAssignBody = z.input<typeof assetAssignInput>;
export type AssetReturnBody = z.input<typeof assetReturnInput>;

export const naidKeys = {
  all: ['naid'] as const,
  list: () => [...naidKeys.all, 'list'] as const,
  detail: (employeeId: string) => [...naidKeys.all, 'detail', employeeId] as const,
};

export const assetKeys = {
  all: ['assets'] as const,
  list: (employeeId?: string) => [...assetKeys.all, 'list', employeeId ?? 'all'] as const,
};

export function useNaidOverview() {
  return useQuery({
    queryKey: naidKeys.list(),
    queryFn: () => api.get<Paginated<NaidOverviewRow>>('/api/naid'),
  });
}

export function useNaidDetail(employeeId: string | undefined) {
  return useQuery({
    queryKey: naidKeys.detail(employeeId ?? ''),
    queryFn: () => api.get<NaidOverviewRow>(`/api/naid/${employeeId}`),
    enabled: Boolean(employeeId),
  });
}

export function useUpdateNaid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ employeeId, body }: { employeeId: string; body: NaidUpdateBody }) =>
      api.put<NaidRecord>(`/api/naid/${employeeId}`, body),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: naidKeys.all });
      void queryClient.invalidateQueries({ queryKey: naidKeys.detail(vars.employeeId) });
    },
  });
}

export function useAssets(employeeId?: string) {
  return useQuery({
    queryKey: assetKeys.list(employeeId),
    queryFn: () =>
      api.get<Paginated<CompanyAsset>>(
        `/api/assets${qs({ employeeId, limit: 100 })}`,
      ),
  });
}

export function useAssignAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AssetAssignBody) => api.post<CompanyAsset>('/api/assets', body),
    onSuccess: (asset) => {
      void queryClient.invalidateQueries({ queryKey: assetKeys.all });
      void queryClient.invalidateQueries({ queryKey: naidKeys.all });
      void queryClient.invalidateQueries({ queryKey: naidKeys.detail(asset.employeeId) });
    },
  });
}

export function useReturnAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: AssetReturnBody }) =>
      api.post<CompanyAsset>(`/api/assets/${id}/return`, body ?? {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: assetKeys.all });
      void queryClient.invalidateQueries({ queryKey: naidKeys.all });
    },
  });
}

export type NaidDocKind = 'yearly-test' | 'nda';

export function naidDocumentContentUrl(employeeId: string, kind: NaidDocKind): string {
  const path = kind === 'yearly-test' ? 'yearly-test-document' : 'nda-document';
  return `/api/naid/${employeeId}/${path}/content`;
}

export function useUploadNaidDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      employeeId,
      kind,
      file,
    }: {
      employeeId: string;
      kind: NaidDocKind;
      file: File;
    }) => {
      const path = kind === 'yearly-test' ? 'yearly-test-document' : 'nda-document';
      const formData = new FormData();
      formData.append('file', file);
      return api.upload<NaidRecord>(`/api/naid/${employeeId}/${path}`, formData);
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: naidKeys.all });
      void queryClient.invalidateQueries({ queryKey: naidKeys.detail(vars.employeeId) });
    },
  });
}
